import { useQuery } from "@tanstack/react-query";

import { fetchHealth, type HealthReport } from "@/lib/api/health";
import { Skeleton, SkeletonGroup } from "@/components/Skeleton";
import { ErrorView } from "@/components/ErrorView";

/**
 * `HealthRoute` — pantalla diagnóstica que consulta el endpoint Go
 * _health y renderiza:
 *
 *   pending → SkeletonGroup con la forma exacta del HealthReport.
 *   error   → ErrorView (reporta a Sentry).
 *   success → estado real, color-coded por status.
 *
 * Es el primer caso testigo del Principio VI: skeleton estructural
 * sin spinners, sin "Loading…", sin layout shift.
 */
export function HealthRoute() {
  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["health"],
    queryFn: ({ signal }) => fetchHealth(signal),
  });

  if (isPending) {
    return (
      <SkeletonGroup label="Cargando estado del sistema…">
        <article className="op-health-card">
          <h2>
            <Skeleton width="14ch" />
          </h2>
          <p>
            <Skeleton block height="1em" width="40%" />
            <Skeleton block height="1em" width="65%" />
          </p>
          <ul>
            <li>
              <Skeleton width="10ch" />
            </li>
            <li>
              <Skeleton width="10ch" />
            </li>
          </ul>
        </article>
      </SkeletonGroup>
    );
  }

  if (isError) {
    return <ErrorView error={error} resetError={() => void refetch()} />;
  }

  return <HealthSummary report={data} refreshing={isFetching} />;
}

function HealthSummary({
  report,
  refreshing,
}: {
  report: HealthReport;
  refreshing: boolean;
}) {
  return (
    <article
      className="op-health-card"
      data-status={report.status}
      data-refreshing={refreshing ? "true" : "false"}
    >
      <h2>Estado: {report.status}</h2>
      <p>
        Versión del esquema: <strong>{report.version}</strong>
      </p>
      <p>
        Marca temporal: <time dateTime={report.timestamp}>{report.timestamp}</time>
      </p>
      <ul>
        <li>database: {report.checks.database}</li>
        <li>sentry: {report.checks.sentry}</li>
      </ul>
    </article>
  );
}
